'use client';

import { useState, FormEvent, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocale } from 'next-intl';
import type { BTCPayInvoice } from '@/types/btcpay';
import { useTranslations } from "next-intl";
import { FaCircleNotch } from "react-icons/fa";
import useFetcherUser from '../checkout/FtecherUser';
import { useSession } from "next-auth/react";
import { GrClose } from "react-icons/gr";
import { FaBitcoin } from "react-icons/fa6";

interface PaymentFormProps {
  onInvoiceCreated?: (invoice: BTCPayInvoice) => void;
}

const localeOfferPaths: Record<string, string> = {
  en: '/account/offer',
  fr: '/compte/offer',
  ar: '/الحساب/العروض',
  nl: '/account/offers',
  de: '/konto/angebote',
  pt: '/conta/ofertas',
  sv: '/konto/erbjudanden',
  ru: '/аккаунт/предложения',
  it: '/account/offerte',
  es: '/cuenta/ofertas',
};

const localeSubdomains: Record<string, string> = {
  en: 'padelmasterthegame.com',
  fr: 'fr.padelmasterthegame.com',
  ar: 'ar.padelmasterthegame.com',
  it: 'it.padelmasterthegame.com',
  ru: 'ru.padelmasterthegame.com',
  de: 'de.padelmasterthegame.com',
  sv: 'sv.padelmasterthegame.com',
  nl: 'nl.padelmasterthegame.com',
  es: 'es.padelmasterthegame.com',
  pt: 'pt.padelmasterthegame.com',
};

export default function PaymentForm({ onInvoiceCreated }: PaymentFormProps) {

  const { data: session } = useSession();
  const { userinfo, isLoading: userinfoLoading } = useFetcherUser();
  const tp = useTranslations('Padel-Master-The-Game-Home-Page');
  const locale = useLocale();

  const [affiliateCode, setAffiliateCode] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [invoice, setInvoice] = useState<BTCPayInvoice | null>(null);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const code = localStorage.getItem('affiliate');
    setAffiliateCode(code);
  }, []);

  // 🔍 DEBUG: confirm session/userinfo state as it changes
  useEffect(() => {
    console.log('🔍 [cryptoOrderHomePAge] session.user:', session?.user);
    console.log('🔍 [cryptoOrderHomePAge] userinfo:', userinfo, 'loading:', userinfoLoading);
  }, [session, userinfo, userinfoLoading]);

  // Poll while popup is open
  useEffect(() => {
    if (!showPopup || !session?.user?.id) return;

    intervalRef.current = setInterval(async () => {
      try {
        const res = await fetch(`/api/payment-status/${session.user.id}`);

        // ✅ Guard: if server returns HTML (404/500 page), don't try to parse JSON
        const contentType = res.headers.get('content-type') ?? '';
        if (!contentType.includes('application/json')) {
          console.warn(
            `⚠️ /api/payment-status/${session.user.id} returned non-JSON (${res.status}). ` +
            `Make sure the route exists at app/api/payment-status/[id]/route.ts`
          );
          return; // keep polling — don't crash
        }

        if (!res.ok) {
          console.warn(`⚠️ payment-status responded with ${res.status}`);
          return;
        }

        const data = await res.json();

        if (data.is_payed) {
          clearInterval(intervalRef.current!);
          const domain = localeSubdomains[locale] ?? 'padelmasterthegame.com';
          const path = localeOfferPaths[locale] ?? '/account/offer';
          window.location.href = `https://${domain}${path}`;
        }
      } catch (err) {
        // Network error (offline, CORS, etc.) — log once and keep polling
        console.warn('Polling network error:', err);
      }
    }, 3000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [showPopup, session?.user?.id, locale]);

  const handleClose = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setShowPopup(false);
  };

  const createInvoice = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setLoading(true);

    console.log('🚨 [cryptoOrderHomePAge] createInvoice fired — this IS the real handler');

    if (!session?.user?.id) {
      alert("User not authenticated");
      setLoading(false);
      return;
    }

    // ✅ This was completely missing before — customerEmail was never
    // resolved or included in the body at all in this file.
    const customerEmail: string | undefined =
      session.user.email ?? userinfo?.email ?? undefined;


    if (!customerEmail) {
      console.warn('⚠️ No email found on session or userinfo — confirmation email will not be sent.');
    }

    try {
      const body: Record<string, any> = {
        amount: "35.47",
        currency: "EUR",
        itemDesc: "Order Created",
        orderId: `order-${Date.now()}`,
        ...(userinfo?.full_name && { customerName: userinfo.full_name }),
        ...(customerEmail && { customerEmail }), // ✅ added
        ...(session.user.id && { userId: session.user.id }),
        ...(affiliateCode && { affiliate_code: affiliateCode }),
        language: locale,
      };

      console.log('Sending invoice body:', body);

      const response = await fetch('/api/invoices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      // ✅ Guard against HTML error pages here too
      const contentType = response.headers.get('content-type') ?? '';
      if (!contentType.includes('application/json')) {
        throw new Error(`Server error ${response.status} — check server logs`);
      }

      const result = await response.json();
      console.log('Invoice API status:', response.status);
      console.log('Invoice API result:', result);

      if (!response.ok || !result.success) {
        // ✅ Show the real error detail from the server if available
        throw new Error(result.details || result.error || 'Failed to create invoice');
      }

      const invoiceData: BTCPayInvoice = result.data;
      setInvoice(invoiceData);
      onInvoiceCreated?.(invoiceData);
      setShowPopup(true);

    } catch (error) {
      console.error('Error creating invoice:', error);
      alert(`Failed to create payment invoice: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const modal = showPopup && invoice && createPortal(
    <div className="fixed inset-0 bg-secondary bg-opacity-50 flex items-center justify-center z-50">
      <div
        className="bg-secondary rounded-lg shadow-xl max-w-5xl h-[80vh] relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-4 border-b">
          <p></p>
          <GrClose
            size={24}
            className="text-b hover:text-white cursor-pointer"
            onClick={handleClose}
          />
        </div>
        <div className="h-[calc(100%-4rem)]">
          <p className="text-white justify-center text-center text-sm font-semibold capitalize mt-4 px-4">
            {tp("tp1")}
            <br />
            {tp("tp2")}
          </p>
          <iframe
            src={invoice.checkoutLink}
            className="w-full h-full"
            title="BTCPay Invoice"
          />
        </div>
      </div>
    </div>,
    document.body
  );

  return (
    <div>
      <form onSubmit={createInvoice} className="space-y-4">
        {loading ? (
          <div className="flex">
            <button className="text-center text-black flex justify-center gap-1 md:gap-3 text-sm md:text-base font-medium w-80 md:w-[28rem] lg:w-[32rem] hover:bg-a hover:text-white items-center border-1 border-a rounded-xl px-4 md:px-4 py-4 transition-all duration-300 ease-in-out cursor-pointer bg-b backdrop-blur-sm hover:scale-110 hover:shadow-[0_0_35px_rgba(255,255,255,0.65)] active:scale-95 animate-pulse-ring">
              <FaBitcoin size={24} />
              {tp("Processing")}{"..."}
              <FaCircleNotch className="ml-3 animate-spin w-5 h-5" />
            </button>
          </div>
        ) : (
          <div className="flex">
            <button
              type="submit"
              disabled={loading}
              className="text-center text-xs md:text-base text-black flex justify-center gap-1 md:gap-3 font-medium w-80 md:w-[28rem] lg:w-[32rem] hover:bg-a hover:text-white items-center border-1 border-a rounded-xl px-4 md:px-4 py-4 transition-all duration-300 ease-in-out cursor-pointer bg-b backdrop-blur-sm hover:scale-110 hover:shadow-[0_0_35px_rgba(255,255,255,0.65)] active:scale-95 animate-pulse-ring"
            >
              <FaBitcoin size={24} />
              {tp("buy-crypto-button")}
            </button>
          </div>
        )}
      </form>

      {modal}
    </div>
  );
}